import type { Translation } from '@/lib/api/common'

/**
 * Point of Sale, as `/v1/merchant/pos` speaks it.
 *
 * Money arrives as a NUMBER here, not a string: the API parses NUMERIC once, in
 * its DTOs, so nothing on this side has to remember that `'18.40' + '21.90'` is
 * a string. Times arrive as ISO strings and stay that way until they are
 * formatted — see `pos-format.ts`.
 */

/** A branch as every `/v1/merchant` endpoint hands it back. */
export interface PosBranchRef {
  id: string
  name: Translation | null
}

/* -------------------------------------------------------------------------- */
/* Registers                                                                  */
/* -------------------------------------------------------------------------- */

export const RegisterStatus = {
  Active: 'active',
  Retired: 'retired',
} as const

export type RegisterStatus = (typeof RegisterStatus)[keyof typeof RegisterStatus]

/**
 * Whether the till is answering. Derived server-side from `lastSeenAt`, never
 * stored — a register that has stopped calling home is exactly the one whose
 * stored status would be stale.
 */
export const RegisterHealth = {
  Active: 'active',
  Offline: 'offline',
  NeverSignedIn: 'never_signed_in',
} as const

export type RegisterHealth = (typeof RegisterHealth)[keyof typeof RegisterHealth]

/** How the till's catalogue compares to the one the back office holds. */
export const CatalogueState = {
  Current: 'current',
  Behind: 'behind',
  /** Offline, so what it holds is unknowable rather than known to be stale. */
  Offline: 'offline',
  /** Claimed but never synced — there is nothing to compare. */
  Unknown: 'unknown',
} as const

export type CatalogueState = (typeof CatalogueState)[keyof typeof CatalogueState]

export interface PosRegister {
  id: string
  code: string
  branchId: string
  branch: PosBranchRef | null
  deviceModel: string | null
  stationName: string | null
  appVersion: string | null
  status: RegisterStatus
  health: RegisterHealth
  catalogue: CatalogueState
  catalogueSyncedAt: string | null
  lastSeenAt: string | null
  firstSeenAt: string | null
  retiredAt: string | null
}

export interface RegisterQuery {
  limit: number
  offset: number
  /** Outlets to narrow to. Omit — never send `[]` — for every outlet in scope. */
  branchIds?: string[]
  status?: RegisterStatus
}

/* -------------------------------------------------------------------------- */
/* The floor                                                                  */
/* -------------------------------------------------------------------------- */

export const TableState = {
  Available: 'available',
  Occupied: 'occupied',
  Ordered: 'ordered',
  Served: 'served',
  Dirty: 'dirty',
  Disabled: 'disabled',
} as const

export type TableState = (typeof TableState)[keyof typeof TableState]

/**
 * The two states the back office owns.
 *
 * The other four belong to the till, which can see the room — the API rejects
 * them from this side, so the form must not offer them.
 */
export const EDITABLE_TABLE_STATES: TableState[] = [TableState.Available, TableState.Disabled]

export interface PosTable {
  id: string
  code: string
  branchId: string
  branch: PosBranchRef | null
  zoneId: string | null
  zoneName: string | null
  seats: number
  status: TableState
  isSeated: boolean
  guestCount: number | null
  seatedAt: string | null
  elapsedSeconds: number | null
  currentOrderId: string | null
  currentOrderTotal: number | null
  qrToken: string | null
}

export interface PosFloorZone {
  zoneId: string | null
  /** The zone's name, or "Unzoned" for the tables nobody has grouped. */
  name: string
  tables: PosTable[]
}

export interface PosFloor {
  branchId: string
  branch: PosBranchRef | null
  zones: PosFloorZone[]
  tableCount: number
  seatedCount: number
}

export interface PosZone {
  id: string
  name: string
  branchId: string
  branch: PosBranchRef | null
  sortOrder: number
  tables: number
  seats: number
}

export interface TableQuery {
  limit: number
  offset: number
  /** Outlets to narrow to. Omit — never send `[]` — for every outlet in scope. */
  branchIds?: string[]
  zoneId?: string
  status?: TableState
}

export interface CreateTableBody {
  branchId: string
  code: string
  zoneId?: string
  seats: number
}

export interface UpdateTableBody {
  code?: string
  /** Explicit null pulls the table out of its zone without deleting either. */
  zoneId?: string | null
  seats?: number
  status?: TableState
}

export interface CreateZoneBody {
  branchId: string
  name: string
  sortOrder?: number
}

export interface UpdateZoneBody {
  name?: string
  sortOrder?: number
}

/* -------------------------------------------------------------------------- */
/* Cash sessions                                                              */
/* -------------------------------------------------------------------------- */

export const SessionStatus = {
  Open: 'open',
  Closed: 'closed',
  /**
   * Ended from the back office rather than by counting the drawer. Its own
   * status because the money was never counted — a report that treated it as a
   * normal close would show a variance of zero it has no right to claim.
   */
  ForceClosed: 'force_closed',
} as const

export type SessionStatus = (typeof SessionStatus)[keyof typeof SessionStatus]

export interface PosSession {
  id: string
  number: string
  registerId: string
  registerCode: string | null
  branchId: string
  branch: PosBranchRef | null
  openedByName: string | null
  openedAt: string
  openingFloat: number
  closedAt: string | null
  closedByName: string | null
  expectedCash: number | null
  countedCash: number | null
  /** counted − expected. Negative is short. Null when nobody counted. */
  variance: number | null
  varianceNeedsAttention: boolean
  status: SessionStatus
  isOpen: boolean
  closedNote: string | null
  durationSeconds: number
  /**
   * What this drawer sold, read off its FROZEN Z — not recomputed.
   *
   * Null while the session is open, and on a force-close that ended a period
   * before any till reported: in both cases no figure was ever frozen, and a
   * number here would be a total nobody signed off.
   */
  netSales: number | null
}

export interface SessionQuery {
  limit: number
  offset: number
  /** Outlets to narrow to. Omit — never send `[]` — for every outlet in scope. */
  branchIds?: string[]
  registerId?: string
  status?: SessionStatus
  /** ISO. Filters on when a session OPENED, not when it closed. */
  from?: string
  to?: string
  /**
   * Only drawers out by more than a rounding difference, either way.
   *
   * Applied by the API, not here. Hiding rows from a page already fetched would
   * leave the count, the paging and the totals describing a different set from
   * the one on screen.
   */
  overTolerance?: boolean
}

/* -------------------------------------------------------------------------- */
/* Shift reports — the X and the Z                                            */
/* -------------------------------------------------------------------------- */

/**
 * Which of the two documents this is.
 *
 * Not a display flag. An X is a reading taken through the side of a drawer that
 * is still trading and changes nothing; a Z is the close itself, frozen, and can
 * never be taken twice. The API sends the same shape for both — see
 * `PosShiftReport` — so this and `final` are the only things separating them.
 */
export const ShiftReportType = {
  X: 'x',
  Z: 'z',
} as const

export type ShiftReportType = (typeof ShiftReportType)[keyof typeof ShiftReportType]

/** A count and an amount, together. Voids, comps and discounts all read this way. */
export interface PosTally {
  count: number
  amount: number
}

/** One row of the payment split. `share` is 0–1, computed server-side. */
export interface PosPaymentSplit {
  method: string
  /** What to print. Unknown methods fall back to the raw key. */
  label: string
  count: number
  amount: number
  /**
   * Share of everything taken, 0–1. Computed by the API so two screens cannot
   * round the same split into two different pie charts.
   */
  share: number
}

/**
 * What a period sold.
 *
 * The three words every POS vendor uses differently, as this API means them:
 * `grossSales` is everything rung up at menu price INCLUDING what was later
 * voided, comped or wasted; `netSales` is that less corrections and discounts —
 * what the business earned; `collected` is net plus tax and service less
 * refunds, and is what `payments` adds up to exactly.
 */
export interface PosSalesFigures {
  grossSales: number
  netSales: number
  /** Lines removed after being sent to the kitchen. */
  voids: PosTally
  /** Given away on purpose — a manager decided. */
  comps: PosTally
  /** Made and binned. Not a sale, not a gift. */
  waste: PosTally
  /** Keyed at the till against one ticket. */
  manualDiscounts: PosTally
  /** Came off a price rule rather than a decision. */
  promotionalDiscounts: PosTally
  refunds: PosTally
  tax: number
  /**
   * The delivery fee. Absent on a Z frozen before the API named it — those
   * carry the same figure under `serviceCharge`.
   */
  delivery?: number
  /**
   * @deprecated Always 0 on a fresh report: NexQR has no service charge, and
   * this key used to hold the delivery fee. Read `delivery`.
   */
  serviceCharge: number
  collected: number
  /** Paid tickets. An open ticket is not takings and is not counted. */
  transactions: number
  averageTicket: number
  /** Largest first. Sums to `collected`. */
  payments: PosPaymentSplit[]
  cash: number
  khqr: number
  /** KHQR as a share of everything taken, 0–1. */
  khqrShare: number
}

/** The drawer a report is about, as the report itself carries it. */
export interface PosShiftRef {
  id: string
  number: string
  registerId: string
  registerCode: string | null
  openedAt: string
  /** Null while the drawer still trades. */
  closedAt: string | null
  openedByName: string | null
  closedByName: string | null
  status: SessionStatus
  openingFloat: number
}

/**
 * The drawer, counted — and three "expected" figures rather than one.
 *
 * `expectedFromSales` is the SERVER's answer: float plus the cash on sales it
 * has actually received. `declaredExpected` is what the TILL said, when a till
 * said anything — the only party that knows about sales it took offline and has
 * not uploaded, so on a register that has been off the network it is the closer
 * figure. `expected` is the one the variance is measured against: the declared
 * figure where there is one, the server's otherwise.
 *
 * Keeping all three is what makes a disagreement visible. On an X over a drawer
 * still trading, `counted` and `variance` are null — nobody has counted yet, and
 * a zero there would read as a drawer that balanced.
 */
export interface PosCashBox {
  openingFloat: number
  cashSales: number
  /** Already off `expectedFromSales`. */
  cashRefunds: number
  expectedFromSales: number
  declaredExpected: number | null
  expected: number
  counted: number | null
  /** counted − expected. Negative is short. Null until somebody counts. */
  variance: number | null
  varianceNeedsAttention: boolean
  note: string | null
}

/**
 * How much of the report rests on a link and how much on a guess.
 *
 * Sales are attributed to a drawer by a stamp written when the ticket was
 * opened. Older sales, and ones from a till that did not name its register,
 * carry no stamp and are matched by the session's WINDOW instead — right for an
 * outlet with one till, and double-counting across an outlet running two.
 * Reported rather than hidden: a figure a manager is about to sign off should
 * say what it is made of.
 */
export interface PosAttribution {
  stamped: number
  byWindow: number
  /** A window-matched sale AND another drawer trading over the same minutes. */
  mayOverlap: boolean
}

/**
 * An X or a Z. One shape for both, because they are the same arithmetic asked at
 * two moments — `final` is what separates them.
 */
export interface PosShiftReport {
  type: ShiftReportType
  /** A Z: the session is closed, this document is frozen, and it cannot be reopened. */
  final: boolean
  shift: PosShiftRef
  branchId: string
  branchName: string | null
  /** On a Z, the instant the drawer closed — the two are one transaction. */
  generatedAt: string
  figures: PosSalesFigures
  cashBox: PosCashBox
  attribution: PosAttribution
}

/**
 * An X taken from the back office.
 *
 * Re-derived at read, every time. On an open drawer it is the same reading the
 * till's own X gives. On a closed one it is a second opinion: the figures come
 * from the orders as they stand NOW, so a refund booked after the close appears
 * here and not in the frozen Z. The two differing is the finding.
 */
export interface PosXReport {
  session: PosSession
  report: PosShiftReport
  /** There is a frozen Z to compare this against. */
  hasZDocument: boolean
}

/**
 * The Z, read back from the frozen document — never recomputed.
 *
 * `report` is null rather than an error on the two rows a manager can
 * legitimately open from the list: a drawer still trading, and one force-closed
 * before any till reported. `device` is the till's own copy of the same close,
 * kept beside the server's to be compared rather than trusted over it.
 */
export interface PosZReport {
  session: PosSession
  report: PosShiftReport | null
  device: Record<string, unknown> | null
}

/* -------------------------------------------------------------------------- */
/* Exceptions                                                                 */
/* -------------------------------------------------------------------------- */

export const ExceptionStatus = {
  Open: 'open',
  Resolved: 'resolved',
  Discarded: 'discarded',
  /** The server settled it. Nobody judged it, so it is not `resolved`. */
  AutoResolved: 'auto_resolved',
} as const

export type ExceptionStatus = (typeof ExceptionStatus)[keyof typeof ExceptionStatus]

export const ExceptionReason = {
  DuplicateAmountsDiffer: 'duplicate_amounts_differ',
  DuplicateAutoResolved: 'duplicate_auto_resolved',
  ItemMissing: 'item_missing',
  OutOfOrder: 'out_of_order',
  Unreadable: 'unreadable',
} as const

export type ExceptionReason = (typeof ExceptionReason)[keyof typeof ExceptionReason]

export const ExceptionResolution = {
  KeptUploaded: 'kept_uploaded',
  KeptExisting: 'kept_existing',
  Dropped: 'dropped',
} as const

export type ExceptionResolution =
  (typeof ExceptionResolution)[keyof typeof ExceptionResolution]

export interface PosException {
  id: string
  number: string
  reason: ExceptionReason
  /** The reason as a sentence. Sent by the API so the two cannot drift. */
  reasonTitle: string
  status: ExceptionStatus
  branchId: string
  branch: PosBranchRef | null
  registerId: string | null
  registerCode: string | null
  sessionId: string | null
  orderNumber: string | null
  amount: number | null
  /** What is already on file, when a duplicate disagrees. The pair is the finding. */
  counterpartAmount: number | null
  detail: string | null
  /**
   * What this row will accept, decided server-side. The screen offers exactly
   * these and nothing else: a button the API would refuse is worse than no
   * button, because somebody presses it while a queue is forming.
   */
  availableResolutions: ExceptionResolution[]
  resolution: ExceptionResolution | null
  resolutionNote: string | null
  resolvedAt: string | null
  createdAt: string
  isOpen: boolean
}

/** One exception with its evidence — the upload, exactly as it arrived. */
export interface PosExceptionDetail extends PosException {
  payload: Record<string, unknown> | null
}

export interface ExceptionQuery {
  limit: number
  offset: number
  /** Outlets to narrow to. Omit — never send `[]` — for every outlet in scope. */
  branchIds?: string[]
  status?: ExceptionStatus
  reason?: ExceptionReason
}

/* -------------------------------------------------------------------------- */
/* Approvals                                                                  */
/* -------------------------------------------------------------------------- */

export const ApprovalAction = {
  Refund: 'refund',
  VoidSentItem: 'void_sent_item',
  PriceOverride: 'price_override',
  Comp: 'comp',
  DrawerNoSale: 'drawer_no_sale',
  CloseSession: 'close_session',
  ReopenSession: 'reopen_session',
  DiscountAboveCap: 'discount_above_cap',
  VoidUnsentItem: 'void_unsent_item',
} as const

export type ApprovalAction = (typeof ApprovalAction)[keyof typeof ApprovalAction]

export const ApprovalRequirement = {
  Free: 'free',
  Manager: 'manager',
} as const

export type ApprovalRequirement =
  (typeof ApprovalRequirement)[keyof typeof ApprovalRequirement]

export const ApprovalOutcome = {
  Granted: 'granted',
  Denied: 'denied',
  Expired: 'expired',
} as const

export type ApprovalOutcome = (typeof ApprovalOutcome)[keyof typeof ApprovalOutcome]

export interface PosApprovalRule {
  action: ApprovalAction
  /** The action as the API names it, so the two lists cannot disagree. */
  label: string
  requirement: ApprovalRequirement
  threshold: number | null
  hasThreshold: boolean
  /**
   * Where the answer came from. `default` means nothing is stored and the
   * server's own fallback applies — which asks for a manager, never grants.
   */
  source: 'branch' | 'tenant' | 'default'
}

export interface PosApprovalPolicy {
  /** Null means this is the tenant default every outlet without its own follows. */
  branchId: string | null
  branch: PosBranchRef | null
  rules: PosApprovalRule[]
}

export interface UpdateApprovalPolicyBody {
  branchId?: string
  rules: {
    action: ApprovalAction
    requirement: ApprovalRequirement
    threshold?: number | null
  }[]
}

export interface PosApprovalRequest {
  id: string
  decidedAt: string
  branchId: string
  branch: PosBranchRef | null
  action: ApprovalAction
  label: string
  amount: number | null
  percentage: number | null
  requestedBy: string | null
  approvedBy: string | null
  outcome: ApprovalOutcome
  /** The till applied the policy alone — "denied — offline", not "denied by". */
  decidedOffline: boolean
  registerId: string | null
  registerCode: string | null
  sessionId: string | null
  note: string | null
}

export interface ApprovalLogQuery {
  limit: number
  offset: number
  /** Outlets to narrow to. Omit — never send `[]` — for every outlet in scope. */
  branchIds?: string[]
  action?: ApprovalAction
  outcome?: ApprovalOutcome
}
